import React, { FC, Fragment, useCallback } from "react";
import clsx from "clsx";
import { FormRenderProps, useForm, useFormState } from "react-final-form";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import Link from "@material-ui/core/Link";
import WeduCheckbox from "../../../../../components/WeduCheckbox/WeduCheckbox";
import DownloadFile from "../../../../../components/DownloadFile/DownloadFile";
import UploadFile from "../../../../../components/UploadFile/UploadFile";
import BootstrapInput from "../../../../../components/BootstrapInput";
import Field from "../../../../../components/Field";
import { mutateErrors } from "../../../../../utils";
import { NotifyMode } from "../../../../notify/NotifyModeEnum";
import { REQUIRED_DOCS } from "../../../../academic/constants";
import { useNotify } from "../../../../notify/useNotify";
import { useApplicationQueries } from "../../../hooks/useApplicationQueries";
import { IApplicationFrontEndModel } from "../../../types";
import { applyAsStep2 } from "./config";
import { WeduLabel } from "../../../../../components/WeduLabel/WeduLabel";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: 400,
    "& > :first-child": {
      marginTop: 0,
    },
  },
  titleContainer: {
    fontWeight: "bold",
    margin: 0,
    marginBottom: 8,

    "& p": {
      margin: 0,
    },
  },
  commentFieldTitle: {
    marginBottom: -8,
  },
  sectionContainer: {
    marginTop: 22,
  },
  applicationFileHint: {
    margin: 0,
    fontSize: 14,
    marginBottom: 8,
  },
  link: {
    fontSize: 16,
    color: "#7D9EF1",
    fontWeight: 500,
    "&:hover": {
      textDecoration: "none",
      color: "#5E88F1",
    },
  },
  hasError: {
    color: "#F94B40",
    fontWeight: "bold",
    width: 125,
  },
  checkboxContent: {
    "&>label": {
      marginLeft: -8,
    },
  },
  reqDocTitle: {
    "& ::first-letter": {
      textTransform: "uppercase",
    },
  },
  uploadFileContainer: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 10,
  },
  uploadErrorContainer: {
    fontSize: 12,
    fontWeight: "bold",
    marginLeft: "auto",
    minHeight: 18,
    color: theme.palette.error.main,
  },
  commentField: {
    paddingTop: 0,
    paddingBottom: 0,
    minHeight: 100,
  },
  requiredDocContainer: {
    "&:not(:last-child)": {
      marginBottom: 27,
    },
  },
  requiredDoqSection: {
    marginTop: 0,
  },
  asterisk: {
    color: "#535358",
    lineHeight: "inherit",
    fontSize: 16,
    fontWeight: "normal",
    marginLeft: 3,
  },
  errorColor: {
    color: theme.palette.error.main,
  },
  weduLabel: {
    color: "#535358",
    fontWeight: "bold",
    marginBottom: 8,
  },
}));

enum DocType {
  ApplicationFile = 1,
  RequiredDoc = 2,
  ExtraDoc = 3,
}

const ApplyApplicationForm: FC<FormRenderProps> = ({ handleSubmit }) => {
  const classes = useStyles();

  const { values }: { values: IApplicationFrontEndModel } = useFormState();
  const { submitErrors } = useFormState();
  const {
    mutators: { push, remove },
    change,
  } = useForm();
  const { uploadApplicationForm, uploadRequiredDocs, uploadExtraDocs } =
    useApplicationQueries();
  const snackNotify = useNotify();

  const applicationLink = values.injectedData.program.applicationLink;
  const stepName = applyAsStep2.stepName!;
  const receivedRequiredDocs = values.injectedData.program.requiredDocs.map(
    (doc) => {
      const foundDoc = REQUIRED_DOCS.find((d) => d.name === doc.name);

      return foundDoc ? { ...doc, name: foundDoc.longName } : doc;
    },
  );
  const linkConfirmationErrorText = submitErrors?.[stepName]?.isLinkConfirmed;
  const isLinkConfirmed = values[stepName]?.isLinkConfirmed || false;
  const hasApplicationFileError =
    Object.keys(submitErrors?.[stepName]?.applicationFiles || {}).length > 0;

  const uploadFile = useCallback(
    (file, docType: DocType) => {
      switch (docType) {
        case DocType.ApplicationFile:
          return uploadApplicationForm(file);
        case DocType.RequiredDoc:
          return uploadRequiredDocs(file);
        case DocType.ExtraDoc:
          return uploadExtraDocs(file);
        default:
          throw new Error("docType not found!");
      }
    },
    [uploadApplicationForm, uploadExtraDocs, uploadRequiredDocs],
  );

  const addNewFiles = useCallback(
    async (files, docType: DocType, docId?) => {
      const succeededFileNames: string[] = [];
      const errorMessages: string[] = [];

      for (const file of files) {
        try {
          let { data } = await uploadFile(file, docType);
          switch (docType) {
            case DocType.ApplicationFile:
              change(`${stepName}.applicationFiles.${docId}`, data);
              break;
            case DocType.RequiredDoc:
              change(`${stepName}.requiredDocs.${docId}`, data);
              break;
            case DocType.ExtraDoc:
              push(`${stepName}.extraDocs`, data);
              break;
            default:
              console.error("docType not found!");
          }
          succeededFileNames.push(file.name);
        } catch (e) {
          const { message } = mutateErrors(e);

          errorMessages.push(`${file.name} - ${message}`);
        }
      }

      if (errorMessages.length > 0) {
        snackNotify("Error", errorMessages.join("\n"), NotifyMode.ERROR);
        return;
      }

      const successMessageEnding =
        succeededFileNames.length > 1
          ? "\nfiles have been uploaded."
          : " has been uploaded.";

      snackNotify(
        "Success",
        succeededFileNames.join("\n") + successMessageEnding,
        NotifyMode.SUCCESS,
      );
    },
    [change, push, snackNotify, stepName, uploadFile],
  );

  const removeFileHandler = useCallback(
    (file, docType: DocType, docId?) => {
      if (docType === DocType.RequiredDoc) {
        return change(`${stepName}.requiredDocs.${docId}`, undefined);
      }
      if (docType === DocType.ExtraDoc) {
        let index = values[stepName].extraDocs?.indexOf(file);
        return remove(`${stepName}.extraDocs`, index);
      }
      if (docType === DocType.ApplicationFile) {
        return change(`${stepName}.applicationFiles.${docId}`, undefined);
      }
    },
    [change, remove, stepName, values],
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      <div className={classes.mainContainer}>
        {!!values.injectedData.program.applicationForms.length && (
          <div className={classes.sectionContainer}>
            <WeduLabel
              label="Application File"
              labelStyles={classes.weduLabel}
              isRequired
              hasError={hasApplicationFileError}
            />
            <p className={classes.applicationFileHint}>
              Please download the file, fill all data and upload it
            </p>
            <Grid container spacing={2}>
              {values.injectedData.program.applicationForms.map(
                (receivedAppForm) => {
                  const hasError =
                    submitErrors?.[stepName]?.applicationFiles?.[
                      receivedAppForm.id
                    ];

                  return (
                    <Fragment key={receivedAppForm.id}>
                      <Grid item xs={12} sm={6}>
                        <DownloadFile file={receivedAppForm} />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        className={classes.uploadFileContainer}
                      >
                        <UploadFile
                          required
                          uploadedFiles={
                            values[stepName]?.applicationFiles?.[
                              receivedAppForm.id
                            ]
                              ? [
                                  values[stepName]?.applicationFiles?.[
                                    receivedAppForm.id
                                  ],
                                ]
                              : []
                          }
                          addNewFiles={(files) =>
                            addNewFiles(
                              files,
                              DocType.ApplicationFile,
                              receivedAppForm.id,
                            )
                          }
                          removeFile={(file) =>
                            removeFileHandler(
                              file,
                              DocType.ApplicationFile,
                              receivedAppForm.id,
                            )
                          }
                          infoText={
                            "You can browse hard copy of application form or/and any docs required."
                          }
                          allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
                          maxSize={30}
                        />
                        <Typography className={classes.uploadErrorContainer}>
                          {hasError}
                        </Typography>
                      </Grid>
                    </Fragment>
                  );
                },
              )}
            </Grid>
          </div>
        )}
        {!!applicationLink && (
          <div className={classes.sectionContainer}>
            <WeduLabel
              label="Application Link"
              labelStyles={classes.weduLabel}
              isRequired
              hasError={!!linkConfirmationErrorText && !isLinkConfirmed}
            />
            <Link
              href={applicationLink}
              className={classes.link}
              target="_blank"
            >
              Get Link Here
            </Link>
            <div className={classes.checkboxContent}>
              <Field
                type="checkbox"
                name={`${stepName}.isLinkConfirmed`}
                hasError={!!linkConfirmationErrorText && !isLinkConfirmed}
                label={
                  "Please fill out all the necessary details and tick the checkbox"
                }
                component={WeduCheckbox}
              />
            </div>
          </div>
        )}
        <div
          className={clsx(classes.sectionContainer, classes.requiredDoqSection)}
        >
          <div className={classes.titleContainer}>
            <p>Required Documents</p>
          </div>
          <Grid container spacing={2}>
            {receivedRequiredDocs.map((receivedRequiredDoc) => (
              <Grid
                item
                xs={12}
                sm={6}
                key={receivedRequiredDoc.id}
                className={classes.requiredDocContainer}
              >
                <span className={classes.reqDocTitle}>
                  {receivedRequiredDoc.name}
                </span>
                <UploadFile
                  uploadedFiles={
                    values[stepName]?.requiredDocs?.[receivedRequiredDoc.id]
                      ? [
                          values[stepName]?.requiredDocs?.[
                            receivedRequiredDoc.id
                          ],
                        ]
                      : []
                  }
                  addNewFiles={(files) =>
                    addNewFiles(
                      files,
                      DocType.RequiredDoc,
                      receivedRequiredDoc.id,
                    )
                  }
                  removeFile={(file) =>
                    removeFileHandler(
                      file,
                      DocType.RequiredDoc,
                      receivedRequiredDoc.id,
                    )
                  }
                  allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
                  maxSize={30}
                />
              </Grid>
            ))}
          </Grid>
        </div>
        <div className={classes.sectionContainer}>
          <div className={classes.titleContainer}>
            <p>Upload any document that will support your application</p>
          </div>
          <UploadFile
            uploadedFiles={values[stepName]?.extraDocs || []}
            addNewFiles={(files) => addNewFiles(files, DocType.ExtraDoc)}
            removeFile={(file) => removeFileHandler(file, DocType.ExtraDoc)}
            infoText={
              "You can browse hard copy of application form or/and any docs required."
            }
            allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
            maxSize={30}
            maxFiles={10}
          />
        </div>
        <div className={classes.sectionContainer}>
          <div className={classes.titleContainer}>
            <p className={classes.commentFieldTitle}>Comment</p>
          </div>
          <Field
            className={classes.commentField}
            rows={4}
            multiline
            fullWidth
            type="textarea"
            name={`${stepName}.comment`}
            placeholder="Description"
            component={BootstrapInput}
          />
        </div>
      </div>
    </form>
  );
};

export default ApplyApplicationForm;
